/**
 * Tasks Section Wrapper
 * 
 * Wraps the Tasks section with an Error Boundary for crash isolation.
 * This component is used within the Notes tab to display the unified task list.
 * 
 * A crash in the tasks section won't affect the rest of the Notes tab.
 */

import React from 'react';

// Error Boundary for crash isolation
class TasksErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[Tasks] Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">
            ⚠️ Tasks Section Error
          </h3>
          <p className="text-amber-600 mb-3">
            Something went wrong loading tasks. The rest of the page is unaffected.
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 text-sm"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides error isolation for the Tasks section
 */
export const TasksSectionWrapper = ({ children }) => {
  return (
    <TasksErrorBoundary>
      {children}
    </TasksErrorBoundary>
  );
};

export default TasksSectionWrapper;
